import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { TextPlugin } from 'gsap/TextPlugin';
import Lenis from 'lenis';
import HeroSection from './components/sections/HeroSection';
import Navbar from './components/layout/Navbar';
import ProjectsSection from './components/sections/ProjectsSection';
import ServicesSection from './components/sections/ServicesSection';
import EducationSection from './components/sections/EducationSection';
import ExperienceSection from './components/sections/ExperienceSection';
import ContactSection from './components/sections/ContactSection';
import Footer from './components/layout/Footer';
import OverviewServiceSection from './components/sections/OverviewServiceSection';
import AboutSection from './components/sections/AboutSection';
import ToolsSection from './components/sections/ToolsSection';
import CertificationsSection from './components/sections/CertificationsSection';
import StickyLabel from './components/layout/StickyLabel';
import CustomCursor from './components/CustomCursor';
import PageTransition from './components/PageTransition';
import './App.css';

gsap.registerPlugin(ScrollTrigger, TextPlugin, SplitText);

ScrollTrigger.defaults({
  toggleActions: 'play none none reverse',
  fastScrollEnd: true,
  preventOverlaps: true,
  invalidateOnRefresh: true,
});

const TickerDivider = () => {
  const tickerRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to('.ticker-inner', {
        x: '-50%',
        repeat: -1,
        duration: 18,
        ease: 'none',
      });
    }, tickerRef);
    return () => ctx.revert();
  }, []);

  const text = "● FULLSTACK DEV ● EAST JAVA ● REACT · NEXT.JS · LARAVEL ● VPS & SERVER ● 99.9% UPTIME ";
  return (
    <div ref={tickerRef} style={{ background: '#111', borderTop: '2px solid #111', borderBottom: '2px solid #111', overflow: 'hidden', padding: '14px 0' }}>
      <div className="ticker-inner" style={{ display: 'flex', width: 'max-content', color: '#FFE600', fontFamily: 'Space Mono, monospace', fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap', letterSpacing: '0.05em' }}>
        {text}{text}{text}{text}
      </div>
    </div>
  );
};

function App() {
  const lenisRef = useRef(null);
  const pageTransitionRef = useRef(null);

  useEffect(() => {
    // ── Lenis smooth scroll ──────────────────────────────────
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });

    lenisRef.current = lenis;
    // Expose to window so child components (e.g. ProjectsSection) can access
    window.lenis = lenis;

    lenis.on('scroll', ScrollTrigger.update);

    const lenisRAF = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(lenisRAF);
    gsap.ticker.lagSmoothing(0);

    // ── Modal scroll isolation: stop/start Lenis on request ──
    const stopLenis  = () => lenis.stop();
    const startLenis = () => lenis.start();
    window.addEventListener('lenis:stop',  stopLenis);
    window.addEventListener('lenis:start', startLenis);
    // ─────────────────────────────────────────────────────────

    // Refresh ScrollTrigger after Lenis initialized
    setTimeout(() => {
      ScrollTrigger.refresh();
    }, 300);

    // Refresh after fonts loaded
    document.fonts.ready.then(() => {
      ScrollTrigger.refresh();
    });
    // ─────────────────────────────────────────────────────────

    // Progress bar
    gsap.to('.progress-bar', {
      width: '100%',
      ease: 'none',
      scrollTrigger: {
        scrub: 0.3,
        start: 'top top',
        end: 'bottom bottom',
      }
    });

    return () => {
      lenis.destroy();
      window.lenis = null;
      gsap.ticker.remove(lenisRAF);
      window.removeEventListener('lenis:stop',  stopLenis);
      window.removeEventListener('lenis:start', startLenis);
    };
  }, []);


  // Handle navigation with page transition
  const handleNavigate = (targetId) => {
    if (pageTransitionRef.current && lenisRef.current) {
      pageTransitionRef.current.triggerTransition(targetId, (id) => {
        // Scroll to target section using Lenis
        const targetElement = document.getElementById(id);
        if (targetElement) {
          lenisRef.current.scrollTo(targetElement, {
            offset: -80,
            duration: 0,
            immediate: true,
          });
        }
      });
    }
  };

  return (
    <div className="App bg-[#F2EFE9] min-h-screen font-jakarta text-[#111] relative">
      {/* Scroll Progress Bar */}
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '4px', background: 'transparent', zIndex: 9999 }}>
        <div className="progress-bar" style={{ width: '0%', height: '100%', background: '#FFE600' }} />
      </div>

      <PageTransition ref={pageTransitionRef} />
      <CustomCursor />
      <StickyLabel />
      <Navbar onNavigate={handleNavigate} />
      <main>
        <HeroSection />
        <OverviewServiceSection />
        <AboutSection />
        <ExperienceSection />
        <EducationSection />
        <CertificationsSection />
        <ToolsSection />
        <TickerDivider />
        <ProjectsSection />
        <ServicesSection />
        <ContactSection />
        <Footer />
      </main>
    </div>
  );
}

export default App;
